import { Container, Row, Col, Card, Badge, Alert, Table } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';

export default function Documentation() {
    return (
        <div style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            minHeight: "100vh",
            color: "white"
        }}>
            <Helmet>
                <title>API Documentation - Headliners News API</title>
                <meta name="description" content="Complete REST API documentation for Headliners News API. Search headlines, filter by source, date range queries. Simple Bearer token authentication." />
                <link rel="canonical" href="https://headlinefeed.dev/documentation" />
            </Helmet>
            <Container className="py-5">
                {/* Header */}
                <Row className="mb-5">
                    <Col>
                        <div className="text-center">
                            <h1 className="display-4 fw-bold mb-3">Headlines API</h1>
                            <p className="lead">
                                Simple REST API for news headlines only
                            </p>
                            <Alert variant="info" className="mx-auto" style={{ maxWidth: "700px" }}>
                                <strong>📰 Headlines Only:</strong> Data and sources growing daily.
                            </Alert>
                            <div className="mt-3">
                                <Badge bg="success" className="px-3 py-2 me-2">
                                    Easy REST API
                                </Badge>
                                <Badge bg="info" className="px-3 py-2 me-2">
                                    Headlines Only
                                </Badge>
                                <Badge bg="warning" text="dark" className="px-3 py-2">
                                    Growing Daily
                                </Badge>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Quick Start */}
                <Row className="mb-5">
                    <Col>
                        <Card className="border-0 shadow" style={{ background: "rgba(255,255,255,0.1)", backdropFilter: "blur(10px)" }}>
                            <Card.Header className="bg-transparent border-0">
                                <h3 className="mb-0">🚀 Quick Start</h3>
                            </Card.Header>
                            <Card.Body>
                                <Alert variant="success" className="mb-3">
                                    <strong>Base URL:</strong> <code>https://api.headlinefeed.dev</code>
                                </Alert>
                                <Alert variant="warning">
                                    <strong>Auth:</strong> Bearer token required
                                </Alert>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Search API Endpoint */}
                <Row className="mb-5">
                    <Col>
                        <Card className="border-0 shadow" style={{ background: "rgba(255,255,255,0.1)", backdropFilter: "blur(10px)" }}>
                            <Card.Header className="bg-transparent border-0">
                                <h3 className="mb-0">🔍 Search Headlines</h3>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-4">
                                    <h5>Endpoint</h5>
                                    <Card className="bg-dark text-light">
                                        <Card.Body>
                                            <code className="fs-6">POST https://api.headlinefeed.dev/api/search</code>
                                        </Card.Body>
                                    </Card>
                                </div>

                                <div className="mb-4">
                                    <h5>Request Body (JSON)</h5>
                                    <Table striped bordered hover variant="dark" size="sm">
                                        <thead>
                                            <tr>
                                                <th>Parameter</th>
                                                <th>Type</th>
                                                <th>Required</th>
                                                <th>Description</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td><code>startDate</code></td>
                                                <td>string</td>
                                                <td>Optional*</td>
                                                <td>Start date (YYYY-MM-DD)</td>
                                            </tr>
                                            <tr>
                                                <td><code>endDate</code></td>
                                                <td>string</td>
                                                <td>Optional*</td>
                                                <td>End date (YYYY-MM-DD)</td>
                                            </tr>
                                            <tr>
                                                <td><code>title</code></td>
                                                <td>string</td>
                                                <td>No</td>
                                                <td>Whole-word search in headline titles (e.g. "ai" matches "AI" but not "air")</td>
                                            </tr>
                                            <tr>
                                                <td><code>source</code></td>
                                                <td>string</td>
                                                <td>No</td>
                                                <td>Filter by news source</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                    <Alert variant="info" className="mt-2">
                                        <small><strong>*</strong> If both dates omitted, defaults to most recent 3 days. If either date provided, both are required. Max 7-day range.</small>
                                    </Alert>
                                </div>

                                <div className="mb-4">
                                    <h5>Example: Date Range Search</h5>
                                    <Card className="bg-dark text-light">
                                        <Card.Body>
                                            <pre className="mb-0">
                                                {`curl -X POST https://api.headlinefeed.dev/api/search \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "startDate": "2025-11-01",
    "endDate": "2025-11-03",
    "title": "technology",
    "source": "techcrunch"
  }'`}
                                            </pre>
                                        </Card.Body>
                                    </Card>
                                </div>

                                <div className="mb-4">
                                    <h5>Example: Recent Headlines in 3 Days (No Dates)</h5>
                                    <Card className="bg-dark text-light">
                                        <Card.Body>
                                            <pre className="mb-0">
                                                {`curl -X POST https://api.headlinefeed.dev/api/search \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"title": "technology"}'`}
                                            </pre>
                                        </Card.Body>
                                    </Card>
                                </div>

                                <div className="mb-4">
                                    <h5>Response</h5>
                                    <Card className="bg-dark text-light">
                                        <Card.Body>
                                            <pre className="mb-0">
                                                {`{
  "headlines": [
    {
      "title": "Tech Company Announces AI Breakthrough",
      "source": "techcrunch",
      "url": "https://techcrunch.com/news/ai-123",
      "publishedAt": "2025-11-01T10:30:00Z",
      "description": "Brief description..."
    }
  ],
  "total": 25,
  "dateRange": {
    "startDate": "2025-11-01T00:00:00.000Z",
    "endDate": "2025-11-03T23:59:59.999Z",
    "days": 3
  }
}`}
                                            </pre>
                                        </Card.Body>
                                    </Card>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Sources API Endpoint */}
                <Row className="mb-5">
                    <Col>
                        <Card className="border-0 shadow" style={{ background: "rgba(255,255,255,0.1)", backdropFilter: "blur(10px)" }}>
                            <Card.Header className="bg-transparent border-0">
                                <h3 className="mb-0">📰 Available Sources</h3>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-4">
                                    <h5>Endpoint</h5>
                                    <Card className="bg-dark text-light">
                                        <Card.Body>
                                            <code className="fs-6">GET https://api.headlinefeed.dev/api/sources</code>
                                        </Card.Body>
                                    </Card>
                                </div>

                                <div className="mb-4">
                                    <h5>Example</h5>
                                    <Card className="bg-dark text-light">
                                        <Card.Body>
                                            <pre className="mb-0">
                                                {`curl https://api.headlinefeed.dev/api/sources`}
                                            </pre>
                                        </Card.Body>
                                    </Card>
                                </div>

                                <div className="mb-4">
                                    <h5>Response</h5>
                                    <Card className="bg-dark text-light">
                                        <Card.Body>
                                            <pre className="mb-0">
                                                {`{
  "sources": ["bbc", "cnn", "techcrunch", "new-york-post", "..."],
  "total": 15,
  "total_headlines": 12847,
  "last_updated": "2025-11-04T10:30:00Z"
}`}
                                            </pre>
                                        </Card.Body>
                                    </Card>
                                </div>

                                <Alert variant="success">
                                    <strong>No Authentication Required:</strong> This endpoint is public and doesn't require a token.
                                </Alert>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Authentication Section */}
                <Row className="mb-5">
                    <Col>
                        <Card className="border-0 shadow" style={{ background: "rgba(255,255,255,0.1)", backdropFilter: "blur(10px)" }}>
                            <Card.Header className="bg-transparent border-0">
                                <h3 className="mb-0">🔐 Authentication</h3>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-4">
                                    <h5>Bearer Token</h5>
                                    <Card className="bg-dark text-light">
                                        <Card.Body>
                                            <code>Authorization: Bearer YOUR_TOKEN</code>
                                        </Card.Body>
                                    </Card>
                                </div>

                                <Alert variant="info">
                                    <strong>Get Token:</strong> Sign up → Dashboard → Generate Token
                                </Alert>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>


                {/* Error Handling */}
                <Row className="mb-5">
                    <Col>
                        <Card className="border-0 shadow" style={{ background: "rgba(255,255,255,0.1)", backdropFilter: "blur(10px)" }}>
                            <Card.Header className="bg-transparent border-0">
                                <h3 className="mb-0">⚠️ Status Codes</h3>
                            </Card.Header>
                            <Card.Body>
                                <Table striped bordered hover variant="dark" size="sm">
                                    <thead>
                                        <tr>
                                            <th>Code</th>
                                            <th>Meaning</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><Badge bg="success">200</Badge></td>
                                            <td>Success</td>
                                        </tr>
                                        <tr>
                                            <td><Badge bg="danger">401</Badge></td>
                                            <td>Invalid token</td>
                                        </tr>
                                        <tr>
                                            <td><Badge bg="danger">429</Badge></td>
                                            <td>Rate limited</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

            </Container>
        </div>
    );
}
